import * as THREE from 'three';
import { ATMO_GLSL, atmoUniforms } from '../atmosphere';

// Rain: streaks in a box that travels with the camera. Each drop's position
// is a pure function of its seed and time (evaluated in the vertex shader),
// so there is no CPU cost at all.

const COUNT = 14000;
const BOX = new THREE.Vector3(46, 34, 46);

const VERT = /* glsl */ `
in vec3 aSeed;
uniform float uTime;
uniform vec3 uBox;
uniform vec3 uCam;
uniform vec3 uFall;
uniform vec3 uStreak;
uniform float uRain;
out float vAlpha;
out vec3 vWorld;
out vec2 vUv;
void main() {
  // Drop position wraps inside a box centred on the camera.
  vec3 p = aSeed * uBox + uFall * uTime * (0.9 + 0.2 * fract(aSeed.x * 91.7));
  p = mod(p - uCam + uBox * 0.5, uBox) - uBox * 0.5 + uCam;
  vec3 v = uStreak;
  float sp = max(length(v), 0.1);
  vec3 d = v / sp;
  vec3 toCam = normalize(cameraPosition - p);
  vec3 side = normalize(cross(d, toCam));
  float len = sp * 0.018;
  vec3 wp = p + side * position.x * 0.012 + d * position.y * len;
  vUv = position.xy + 0.5;
  float visible = step(fract(aSeed.y * 7.13), uRain);
  float dist = length(p - cameraPosition);
  vAlpha = visible * smoothstep(0.5, 2.0, dist) * (1.0 - smoothstep(14.0, 23.0, dist));
  vWorld = wp;
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const FRAG = /* glsl */ `
${ATMO_GLSL}
in float vAlpha;
in vec3 vWorld;
in vec2 vUv;
void main() {
  float a = vAlpha * (1.0 - abs(vUv.x - 0.5) * 2.0) * smoothstep(0.0, 0.3, vUv.y) * 0.32;
  if (a < 0.002) discard;
  vec3 col = uSkyAmbient * 0.9 + uSunColor * 0.08 + vec3(0.8, 0.85, 1.0) * uFlash.w * 2.0;
  gl_FragColor = vec4(col, a);
}
`;

export class Rain {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor() {
    const base = new THREE.PlaneGeometry(1, 1);
    const g = new THREE.InstancedBufferGeometry();
    g.index = base.index;
    g.setAttribute('position', base.getAttribute('position'));
    const seeds = new Float32Array(COUNT * 3);
    for (let i = 0; i < seeds.length; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 3));
    g.instanceCount = COUNT;
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        ...atmoUniforms,
        uTime: { value: 0 },
        uBox: { value: BOX },
        uCam: { value: new THREE.Vector3() },
        uFall: { value: new THREE.Vector3(0, -9, 0) },
        uStreak: { value: new THREE.Vector3(0, -9, 0) },
        uRain: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 30;
  }

  update(time: number, camera: THREE.Camera, rain: number, windX: number, windZ: number, windSpeed: number, shipVel: THREE.Vector3, onShip: boolean): void {
    const u = this.material.uniforms;
    u.uTime!.value = time;
    u.uCam!.value.copy(camera.position);
    u.uFall!.value.set(windX * windSpeed * 0.8, -8.5, windZ * windSpeed * 0.8);
    // Streaks follow the motion relative to the viewer (who may be aboard).
    const rel = onShip ? 1 : 0;
    u.uStreak!.value.copy(u.uFall!.value).addScaledVector(shipVel, -rel);
    u.uRain!.value = rain;
    this.mesh.visible = rain > 0.01;
  }
}
